// src/hooks/useCompaniesMap.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Hook for the company map.
 * Builds Leaflet marker data from companies.
 * Only ACTIVE companies with valid coordinates are included.
 */
export default function useCompaniesMap(filters = {}) {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function fetchFilteredCompanies() {
            setLoading(true);
            setError("");
            try {
                const params = {};
                Object.entries(filters || {}).forEach(([key, value]) => {
                    if (value === undefined || value === null || value === "" || value === "all") return;
                    if (Array.isArray(value)) {
                        if (value.length) params[key] = value.join(",");
                        return;
                    }
                    params[key] = value;
                });

                const res = await axios.get(`${API_BASE}/api/filteredcompanies`, {
                    withCredentials: true,
                    params,
                });
                if (!cancelled) setCompanies(res.data || []);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.error ||
                        err?.message ||
                        "Unternehmen konnten nicht geladen werden."
                    );
                    setCompanies([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchFilteredCompanies();

        return () => {
            cancelled = true;
        };
    }, [filters]);

    // Comments in English: Normalize numeric input; reject empty/"null"/"undefined".
    const toFiniteNumberOrNull = (val) => {
        if (val === null || val === undefined) return null;
        if (typeof val === "string") {
            const s = val.trim().toLowerCase();
            if (!s || s === "null" || s === "undefined" || s === "nan") return null;
        }
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
    };

    // Comments in English: Validate lat/lon including allowed ranges.
    const isValidLatLon = (lat, lon) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
        if (lat < -90 || lat > 90) return false;
        if (lon < -180 || lon > 180) return false;
        if (lat === 0 && lon === 0) return false;
        return true;
    };

    const markers = useMemo(() => {
        if (!Array.isArray(companies)) return [];

        return companies
            .filter((c) => {
                if (!c) return false;
                const v = c.is_active;
                if (v === undefined || v === null) return true;
                return v === true || v === 1 || v === "1" || v === "true";
            })
            .map((c) => {
                const lat = toFiniteNumberOrNull(c.latitude ?? c.lat);
                const lon = toFiniteNumberOrNull(c.longitude ?? c.lng);
                if (!isValidLatLon(lat, lon)) return null;

                return {
                    id: c.id,
                    slug: c.slug || "",
                    name: c.name || "",
                    street: c.street || "",
                    zip: c.zip || "",
                    city: c.city || "",
                    country: c.country || "",
                    // Use absolute URL from backend if available
                    startImage:
                        c.start_image_url_abs ||
                        c.start_image_url ||
                        null,
                    startImageCrop: c.start_image_crop || null,
                    position: [lat, lon],
                };
            })
            .filter(Boolean);
    }, [companies]);

    return {
        companies,
        markers,
        loading,
        error,
    };
}
